import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, Zoom, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/zoom';
import 'swiper/css/effect-fade';
import { getFileType } from './utils/photoHelpers';
import { useVideoManager } from './hooks/useVideoManager';
import { useMediaViewUrls } from './hooks/useMediaViewUrls';
import VideoPlayer from './VideoPlayer';
import DownloadProgressOverlay from './DownloadProgressOverlay';
import { useSingleFileDownload } from './hooks/useSingleFileDownload';
import useDialogFocus from '../../hooks/useDialogFocus';

/**
 * PhotoModal component handles the photo viewing modal with Swiper carousel
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Array} props.photos - Array of photo filenames
 * @param {number} props.currentIndex - Current photo index
 * @param {function} props.onClose - Handler for closing the modal
 * @param {function} props.onSlideChange - Handler for slide changes
 * @param {boolean} props.hasNextPage - Whether there are more photos to load
 * @param {function} props.fetchNextPage - Function to fetch next page
 * @param {boolean} props.autoPlay - Whether to auto-play videos
 * @param {number} props.totalPhotos - Total number of photos available
 * @returns {JSX.Element|null}
 */
const PhotoModal = ({
  isOpen,
  photos,
  currentIndex,
  onClose,
  onSlideChange,
  hasNextPage,
  fetchNextPage,
  autoPlay = false,
  totalPhotos = 0,
  galleryId
}) => {
  const closeButtonRef = useRef(null);
  const modalRef = useDialogFocus(isOpen && Boolean(photos), onClose, closeButtonRef);
  const swiperRef = useRef(null);
  const retryRequestRef = useRef(new Map());
  const viewerGenerationRef = useRef(0);
  const previousGalleryRef = useRef(galleryId);
  const [activeSlideIndex, setActiveSlideIndex] = useState(currentIndex);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [retryingImages, setRetryingImages] = useState(() => new Set());
  const [imageReloads, setImageReloads] = useState(() => new Map());
  const latestSlideRef = useRef({ activeSlideIndex, galleryId, isOpen, photos });
  latestSlideRef.current = { activeSlideIndex, galleryId, isOpen, photos };

  // Create photoAccess object for compatibility
  const photoAccess = useMemo(() => {
    if (!photos) return null;
    return {
      getAllLoadedPhotos: () => photos,
      getPhotoByIndex: (index) => photos[index],
      totalPhotos: totalPhotos || photos.length // Use totalPhotos if available, fallback to loaded photos count
    };
  }, [photos, totalPhotos]);

  // Get video files for the video manager - only nearby videos for lazy loading
  const videoFiles = useMemo(() => {
    if (!photoAccess) return [];

    const allPhotos = photoAccess.getAllLoadedPhotos();
    const totalPhotos = allPhotos.length;

    // Only manage videos that are near the active slide index (active ± 2) for lazy loading
    const startIndex = Math.max(0, activeSlideIndex - 2);
    const endIndex = Math.min(totalPhotos - 1, activeSlideIndex + 2);

    const nearbyVideos = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const filename = allPhotos[i];
      if (getFileType(filename) === 'video') {
        nearbyVideos.push(filename);
      }
    }

    return nearbyVideos;
  }, [photoAccess, activeSlideIndex]);

  // Update active slide index when currentIndex prop changes
  useEffect(() => {
    setActiveSlideIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    const galleryChanged = previousGalleryRef.current !== galleryId;
    previousGalleryRef.current = galleryId;
    viewerGenerationRef.current += 1;
    retryRequestRef.current.clear();
    if (!isOpen || galleryChanged) {
      setFailedImages(new Set());
      setRetryingImages(new Set());
      setImageReloads(new Map());
    }
  }, [galleryId, isOpen]);

  useEffect(() => () => {
    viewerGenerationRef.current += 1;
    retryRequestRef.current.clear();
    latestSlideRef.current = { ...latestSlideRef.current, isOpen: false };
  }, []);

  // Use video manager hook
  const { setVideoPlayers, getStreamData, isStreamLoading, cleanupPlayers } = useVideoManager(videoFiles, isOpen, galleryId);

  // Use signed view URLs hook (lazy full-size image loading + signed thumbnails)
  const {
    getViewUrl,
    getThumbnailUrl: getSignedThumbnailUrl,
    hasViewUrlError,
    refreshViewUrl,
  } = useMediaViewUrls(photos, activeSlideIndex, isOpen, galleryId);

  // Use single file download hook
  const { downloadFile, downloadState } = useSingleFileDownload(galleryId);

  // Swiper event handlers
  const handleSwiperSlideChange = (swiper) => {
    const newIndex = swiper.activeIndex;
    setActiveSlideIndex(newIndex);
    onSlideChange(newIndex);

    // Stop all videos when changing slides to prevent audio bleed
    const allVideos = document.querySelectorAll('video[data-filename]');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Auto-play video when navigating to video slide
    const currentFilename = photoAccess?.getPhotoByIndex(newIndex);
    if (currentFilename && getFileType(currentFilename) === 'video') {
      // Play video after slide transition completes
      setTimeout(() => {
        const videoElement = document.querySelector(`video[data-filename="${currentFilename}"]`);
        if (videoElement && swiper.activeIndex === newIndex) {
          // Play from beginning
          videoElement.currentTime = 0;
          // Use play() promise to handle autoplay restrictions
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video autoplay failed in PhotoModal navigation:', error);
              // User interaction required - video will need manual play
            });
          }
        }
      }, 800); // Increased timeout for video element initialization
    }

    // Load more photos if we're near the end of loaded photos
    if (photoAccess) {
      const loadedPhotosCount = photoAccess.getAllLoadedPhotos().length;
      if (newIndex >= loadedPhotosCount - 5 && hasNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleSwiperInit = (swiper) => {
    swiperRef.current = swiper;
    // Navigate to the correct slide when modal opens
    swiper.slideTo(currentIndex, 0);

    // Auto-play if initial slide is a video - increased timeout for video to load
    const initialFilename = photoAccess?.getPhotoByIndex(currentIndex);
    if (initialFilename && getFileType(initialFilename) === 'video') {
      setTimeout(() => {
        const videoElement = document.querySelector(`video[data-filename="${initialFilename}"]`);
        if (videoElement) {
          videoElement.currentTime = 0;
          // Use play() promise to handle autoplay restrictions
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video autoplay failed on PhotoModal open:', error);
              // User interaction required - video will need manual play
            });
          }
        }
      }, 800); // Increased timeout to allow video element to fully initialize
    }
  };

  const handleImageError = (index, filename) => {
    const latestSlide = latestSlideRef.current;
    if (
      latestSlide.isOpen
      && latestSlide.galleryId === galleryId
      && latestSlide.photos?.[index] === filename
    ) {
      const imageKey = JSON.stringify([galleryId, index, filename]);
      setFailedImages((current) => {
        if (current.has(imageKey)) return current;
        const next = new Set(current);
        next.add(imageKey);
        return next;
      });
    }
  };

  const handleImageRetry = async (index, filename) => {
    const imageKey = JSON.stringify([galleryId, index, filename]);
    const requestId = (retryRequestRef.current.get(imageKey) || 0) + 1;
    const viewerGeneration = viewerGenerationRef.current;
    retryRequestRef.current.set(imageKey, requestId);
    setRetryingImages((current) => new Set(current).add(imageKey));

    try {
      await refreshViewUrl(filename);
      const latestSlide = latestSlideRef.current;
      if (
        viewerGenerationRef.current === viewerGeneration
        && retryRequestRef.current.get(imageKey) === requestId
        && latestSlide.isOpen
        && latestSlide.galleryId === galleryId
        && latestSlide.photos?.[index] === filename
      ) {
        setImageReloads((current) => {
          const next = new Map(current);
          next.set(imageKey, (current.get(imageKey) || 0) + 1);
          return next;
        });
        setFailedImages((current) => {
          if (!current.has(imageKey)) return current;
          const next = new Set(current);
          next.delete(imageKey);
          return next;
        });
      }
    } catch {
      const latestSlide = latestSlideRef.current;
      if (
        viewerGenerationRef.current === viewerGeneration
        && retryRequestRef.current.get(imageKey) === requestId
        && latestSlide.isOpen
        && latestSlide.galleryId === galleryId
        && latestSlide.photos?.[index] === filename
      ) {
        setFailedImages((current) => new Set(current).add(imageKey));
      }
    } finally {
      if (
        viewerGenerationRef.current === viewerGeneration
        && retryRequestRef.current.get(imageKey) === requestId
      ) {
        setRetryingImages((current) => {
          const next = new Set(current);
          next.delete(imageKey);
          return next;
        });
      }
    }
  };

  // Helper function to render photo/video content with lazy loading
  const renderPhotoContent = (index) => {
    if (!photoAccess) return null;
    const filename = photoAccess.getPhotoByIndex(index);
    const fileType = getFileType(filename);

    // Implement lazy loading: only load content for nearby slides
    const isNearCurrentSlide = Math.abs(index - activeSlideIndex) <= 1;

    if (fileType === 'video') {
      const streamData = getStreamData(filename);
      const isLoading = isStreamLoading(filename);

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="video-container w-full max-w-6xl mx-auto">
            <VideoPlayer
              filename={filename}
              streamData={streamData}
              isLoading={isLoading}
              showControls={true}
              autoPlay={false} // Always false - manual play handled by handleSwiperInit/handleSwiperSlideChange
              muted={false}
              preload={isNearCurrentSlide ? "metadata" : "none"} // Only preload metadata for nearby videos
              className="w-full max-h-[80vh] rounded shadow-lg"
              style={{ objectFit: 'contain', maxWidth: '100%', height: 'auto' }}
              setVideoPlayers={setVideoPlayers}
              posterUrl={getSignedThumbnailUrl(filename)}
            />
          </div>
        </div>
      );
    } else {
      const signedUrl = getViewUrl(filename);
      const imageKey = JSON.stringify([galleryId, index, filename]);

      if (!isNearCurrentSlide) {
        // For distant slides, show a placeholder
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="w-full h-auto max-w-4xl max-h-[80vh] rounded shadow-lg bg-gray-200 flex items-center justify-center">
              <div className="text-gray-500 text-lg">Loading...</div>
            </div>
          </div>
        );
      }

      const hasImageError = failedImages.has(imageKey) || hasViewUrlError(filename);
      const showImageError = hasImageError && activeSlideIndex === index;

      if (!signedUrl && !showImageError) {
        // Signed URL not ready yet — keep placeholder instead of a 403 direct URL
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="w-full h-auto max-w-4xl max-h-[80vh] rounded shadow-lg bg-gray-200 flex items-center justify-center">
              <div className="text-gray-500 text-lg">Loading...</div>
            </div>
          </div>
        );
      }

      if (showImageError) {
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div
              className="rounded-lg bg-black-60 px-6 py-5 text-center text-white shadow-lg"
              role="alert"
              aria-live="assertive"
            >
              <p className="text-base font-medium">Unable to load photo</p>
              <button
                type="button"
                onClick={() => handleImageRetry(index, filename)}
                disabled={retryingImages.has(imageKey)}
                className="mt-3 min-h-[44px] rounded-full border border-white-30 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white-20 disabled:cursor-wait disabled:opacity-70"
              >
                {retryingImages.has(imageKey) ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            key={`${imageKey}-${imageReloads.get(imageKey) || 0}`}
            src={signedUrl}
            loading="lazy"
            className="w-full h-auto max-w-4xl rounded shadow-lg"
            style={{ maxHeight: '80vh', objectFit: 'contain' }}
            alt={`${index + 1} of ${photoAccess?.totalPhotos || 0}`}
            onError={() => handleImageError(index, filename)}
          />
        </div>
      );
    }
  };

  // Cleanup Plyr players on unmount
  useEffect(() => {
    return () => {
      cleanupPlayers();
    };
  }, [cleanupPlayers]);

  // Handle download with proper cross-browser support using presigned URLs
  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const filename = photoAccess.getPhotoByIndex(currentIndex);

    try {
      // Always request a fresh /download-urls URL. The playback stream URL may
      // be near expiry and has an inline streaming disposition, so reusing it
      // for a download would serve a stale or streamed copy.
      await downloadFile(filename);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  if (!isOpen || !photoAccess) return null;

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label="Photo and video viewer"
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 photo-modal"
      onClick={onClose}
    >
      <div className="relative w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
        {/* Swiper Carousel */}
        <Swiper
          onSwiper={handleSwiperInit}
          onSlideChange={handleSwiperSlideChange}
          initialSlide={currentIndex}
          modules={[Navigation, Keyboard, Zoom, EffectFade]}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          zoom={{
            maxRatio: 3,
            minRatio: 0.5,
          }}
          spaceBetween={0}
          slidesPerView={1}
          centeredSlides={true}
          loop={false}
          speed={400}
          allowTouchMove={true}
          resistance={true}
          resistanceRatio={0.85}
          effect="fade"
          fadeEffect={{
            crossFade: true
          }}
          className="w-full h-full"
        >
          {Array.from({ length: photos?.length || 0 }, (_, index) => (
            <SwiperSlide
              key={`photo-${index}`}
              inert={index !== activeSlideIndex ? true : undefined}
              aria-hidden={index !== activeSlideIndex ? true : undefined}
            >
              <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
                {renderPhotoContent(index)}
              </div>
            </SwiperSlide>
          ))}

          {/* Navigation buttons */}
          <div className="swiper-button-prev !text-white !opacity-70 hover:!opacity-100 transition-opacity"></div>
          <div className="swiper-button-next !text-white !opacity-70 hover:!opacity-100 transition-opacity"></div>
        </Swiper>

        {/* Modal header with floating icon buttons */}
        <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 py-4 z-10 pointer-events-none">
          <div className="flex items-center space-x-3">
            {photoAccess && (
              <button
                onClick={handleDownload}
                title="Download original file"
                className="pointer-events-auto group"
                aria-label="Download photo"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white-20 backdrop-blur-md border border-white-20 shadow-lg transition-all duration-200 group-hover:bg-white-30 group-hover:scale-105">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </span>
              </button>
            )}
          </div>

          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close photo viewer"
            className="pointer-events-auto group"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white-20 backdrop-blur-md border border-white-20 shadow-lg transition-all duration-200 group-hover:bg-white-30 group-hover:scale-105">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          </button>
        </div>

        {/* Photo counter */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
          <div className="px-6 py-3 rounded-full bg-black-60 backdrop-blur-md text-white text-sm font-medium border border-white-20 shadow-lg">
            <span className="font-semibold">{currentIndex + 1}</span>
            <span className="text-white-70 mx-1">of</span>
            <span>{photoAccess?.totalPhotos || 0}</span>
          </div>
        </div>
      </div>

      {/* Download Progress Overlay */}
      <DownloadProgressOverlay downloadState={downloadState} />
    </div>
  );
};

export default PhotoModal;
